import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import type { IconCategory, MapArrow, MapMode, MapObject } from '@warplan/shared';
import { ICON_CATEGORIES } from '@warplan/shared';
import { ICON_CATALOG, getIconEntry } from '../lib/icon-catalog.js';
import { getCategoryStroke } from '../lib/category-colors.js';
import { findRegionKey, findRegionLabel } from '../lib/foxhole-maps.js';
import { useAssetPanel, type AssetGroupKey } from '../state/asset-panel-store.js';

export interface WarLabel {
  wx: number;
  wy: number;
  text: string;
  kind: 'major' | 'minor';
}

export interface AssetListPanelProps {
  objects: readonly MapObject[];
  /** All arrows (visibility filter is applied here, not by the caller). */
  arrows?: readonly MapArrow[];
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
  /** Region keys to hide from the panel (admin-disabled hexagons). */
  disabledRegionKeys?: ReadonlySet<string>;
  /** Active view mode. Defaults to 'planner' for backward compat. */
  mode?: MapMode;
}

// ---------------------------------------------------------------------------
// Icon filter sets
// ---------------------------------------------------------------------------

const HUB_IDS = new Set(['LOGISTIC_HUB', 'LOGISTIC_SEAPORT', 'LOGISTIC_STORAGEDEPOT', 'LOGISTIC_AIRWAY']);

const MSUP_IDS = new Set([
  'LOGISTIC_MAINTENANCETUNNEL',
  'LOGISTIC_MSUP_5K',
  'LOGISTIC_MSUP_10K',
  'LOGISTIC_MSUP_30K',
  'FACILITY_MSUP',
]);

const TUNNEL_ID = 'LOGISTIC_MAINTENANCETUNNEL';
const MSUP_VARIANT_IDS = new Set(['LOGISTIC_MSUP_5K', 'LOGISTIC_MSUP_10K', 'LOGISTIC_MSUP_30K']);
const MSUP_VARIANT_SHORT: Record<string, string> = {
  LOGISTIC_MSUP_5K: '5K',
  LOGISTIC_MSUP_10K: '10K',
  LOGISTIC_MSUP_30K: '30K',
};

/** World-pixel radius within which a MSup variant is paired with a tunnel. */
const MERGE_RADIUS_SQ = 30 * 30;

const MATERIAL_ID = 'FACILITY_MATERIAL';
const ASSMAT_IDS = new Set([
  'FACILITY_ASSMAT_1',
  'FACILITY_ASSMAT_2',
  'FACILITY_ASSMAT_3',
  'FACILITY_ASSMAT_4',
  'FACILITY_ASSMAT_5',
]);
const ASSMAT_SHORT: Record<string, string> = {
  FACILITY_ASSMAT_1: 'AM1',
  FACILITY_ASSMAT_2: 'AM2',
  FACILITY_ASSMAT_3: 'AM3',
  FACILITY_ASSMAT_4: 'AM4',
  FACILITY_ASSMAT_5: 'AM5',
};
/** World-pixel radius for Material + Assmat pairing. */
const FACILITY_MERGE_RADIUS_SQ = 30 * 30;

type MsupDisplayItem =
  | { kind: 'merged'; tunnel: MapObject; msup: MapObject }
  | { kind: 'standalone'; obj: MapObject };

type FacilityDisplayItem =
  | { kind: 'merged'; material: MapObject; assmats: MapObject[] }
  | { kind: 'standalone'; obj: MapObject };

function isHub(obj: MapObject): boolean {
  return HUB_IDS.has(obj.metadata.iconId);
}

function isBuilds(obj: MapObject): boolean {
  return obj.kind === 'build';
}

function isCombat(obj: MapObject): boolean {
  return obj.kind === 'combat';
}

function isFacility(obj: MapObject): boolean {
  return obj.kind === 'facility' && !MSUP_IDS.has(obj.metadata.iconId);
}

function isMsup(obj: MapObject): boolean {
  return MSUP_IDS.has(obj.metadata.iconId);
}

function isNavy(obj: MapObject): boolean {
  return obj.kind === 'navy';
}

// ---------------------------------------------------------------------------
// Location lookup helpers (pure, O(n) distance search)
// ---------------------------------------------------------------------------

function distSq(ax: number, ay: number, bx: number, by: number): number {
  return (ax - bx) ** 2 + (ay - by) ** 2;
}

function findRegion(wx: number, wy: number): string {
  return findRegionLabel(wx, wy);
}

function findNearestLabel(
  wx: number,
  wy: number,
  warLabels: readonly WarLabel[],
  kind: 'major' | 'minor',
): string {
  let best: WarLabel | null = null;
  let bestDist = Infinity;
  for (const lbl of warLabels) {
    if (lbl.kind !== kind) continue;
    const d = distSq(wx, wy, lbl.wx, lbl.wy);
    if (d < bestDist) {
      bestDist = d;
      best = lbl;
    }
  }
  return best?.text ?? '—';
}

/**
 * Pair each Maintenance Tunnel with the closest MSup variant within
 * MERGE_RADIUS_SQ. Unmatched tunnels and variants are kept standalone.
 * FACILITY_MSUP entries are always standalone.
 */
function buildMsupItems(msupObjects: readonly MapObject[]): MsupDisplayItem[] {
  const tunnels = msupObjects.filter((o) => o.metadata.iconId === TUNNEL_ID);
  const variants = msupObjects.filter((o) => MSUP_VARIANT_IDS.has(o.metadata.iconId));
  const facilityMsup = msupObjects.filter(
    (o) => !MSUP_VARIANT_IDS.has(o.metadata.iconId) && o.metadata.iconId !== TUNNEL_ID,
  );

  const usedVariantIds = new Set<string>();
  const result: MsupDisplayItem[] = [];

  for (const tunnel of tunnels) {
    let bestVariant: MapObject | null = null;
    let bestDist = MERGE_RADIUS_SQ;
    for (const v of variants) {
      if (usedVariantIds.has(v.id)) continue;
      const d = distSq(tunnel.position.x, tunnel.position.y, v.position.x, v.position.y);
      if (d <= bestDist) {
        bestDist = d;
        bestVariant = v;
      }
    }
    if (bestVariant) {
      usedVariantIds.add(bestVariant.id);
      result.push({ kind: 'merged', tunnel, msup: bestVariant });
    } else {
      result.push({ kind: 'standalone', obj: tunnel });
    }
  }

  for (const v of variants) {
    if (!usedVariantIds.has(v.id)) result.push({ kind: 'standalone', obj: v });
  }

  for (const fm of facilityMsup) {
    result.push({ kind: 'standalone', obj: fm });
  }

  return result;
}

/**
 * Pair each Material with ALL Assmat icons within FACILITY_MERGE_RADIUS_SQ.
 * Unmatched Materials and Assmats are kept standalone.
 */
function buildFacilityItems(facilityObjects: readonly MapObject[]): FacilityDisplayItem[] {
  const materials = facilityObjects.filter((o) => o.metadata.iconId === MATERIAL_ID);
  const assmats = facilityObjects.filter((o) => ASSMAT_IDS.has(o.metadata.iconId));
  const others = facilityObjects.filter(
    (o) => o.metadata.iconId !== MATERIAL_ID && !ASSMAT_IDS.has(o.metadata.iconId),
  );

  const usedAssmatIds = new Set<string>();
  const result: FacilityDisplayItem[] = [];

  for (const mat of materials) {
    const nearby = assmats.filter(
      (a) =>
        !usedAssmatIds.has(a.id) &&
        distSq(mat.position.x, mat.position.y, a.position.x, a.position.y) <= FACILITY_MERGE_RADIUS_SQ,
    );
    if (nearby.length > 0) {
      nearby.forEach((a) => usedAssmatIds.add(a.id));
      // Deduplicate by iconId (one AM4 even if two AM4s are nearby).
      const seenTypes = new Set<string>();
      const uniqueAssmats = nearby.filter((a) => {
        if (seenTypes.has(a.metadata.iconId)) return false;
        seenTypes.add(a.metadata.iconId);
        return true;
      });
      const sortedAssmats = [...uniqueAssmats].sort((a, b) =>
        (ASSMAT_SHORT[a.metadata.iconId] ?? '').localeCompare(ASSMAT_SHORT[b.metadata.iconId] ?? ''),
      );
      result.push({ kind: 'merged', material: mat, assmats: sortedAssmats });
    } else {
      result.push({ kind: 'standalone', obj: mat });
    }
  }

  for (const a of assmats) {
    if (!usedAssmatIds.has(a.id)) result.push({ kind: 'standalone', obj: a });
  }

  for (const o of others) {
    result.push({ kind: 'standalone', obj: o });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ALPHA_COLLATOR = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

function getAssetLabel(obj: MapObject): string {
  const note = obj.metadata.note;
  if (note && note.trim().length > 0) return note;
  return getIconEntry(obj.kind, obj.metadata.iconId).label;
}

function getMsupItemLabel(item: MsupDisplayItem): string {
  if (item.kind === 'merged') {
    const short = MSUP_VARIANT_SHORT[item.msup.metadata.iconId] ?? item.msup.metadata.iconId;
    const base =
      item.tunnel.metadata.note && item.tunnel.metadata.note.trim().length > 0
        ? item.tunnel.metadata.note
        : 'Maintenance Tunnel';
    return `${base} (${short})`;
  }
  return getAssetLabel(item.obj);
}

function getFacilityItemLabel(item: FacilityDisplayItem): string {
  if (item.kind === 'merged') {
    const base =
      item.material.metadata.note && item.material.metadata.note.trim().length > 0
        ? item.material.metadata.note
        : 'Material';
    return base;
  }
  return getAssetLabel(item.obj);
}

interface AssetItemProps {
  obj: MapObject;
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
}

function AssetItem({ obj, warLabels, onNavigate }: AssetItemProps): React.JSX.Element {
  const entry = getIconEntry(obj.kind, obj.metadata.iconId);
  const wx = obj.position.x;
  const wy = obj.position.y;
  const region = findRegion(wx, wy);
  const major = findNearestLabel(wx, wy, warLabels, 'major');
  const minor = findNearestLabel(wx, wy, warLabels, 'minor');
  const note = obj.metadata.note;

  return (
    <button
      type="button"
      onClick={() => onNavigate(wx, wy)}
      title={`Naviguer vers ${entry.label}`}
      className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-slate-100 transition hover:bg-slate-700/60 active:bg-slate-700"
    >
      <img
        src={entry.src}
        alt=""
        className="mt-0.5 h-5 w-5 shrink-0 object-contain"
        draggable={false}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold leading-tight">
          {note ? note : entry.label}
        </span>
        <span className="block truncate text-[12px] leading-tight text-slate-300">
          {region}
          {major !== '—' ? ` / ${major}` : ''}
        </span>
        <span className="block truncate text-[12px] leading-tight text-slate-400">{minor}</span>
      </span>
    </button>
  );
}

interface MsupAssetItemProps {
  item: MsupDisplayItem;
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
}

function MsupAssetItem({ item, warLabels, onNavigate }: MsupAssetItemProps): React.JSX.Element {
  if (item.kind === 'merged') {
    const { tunnel, msup } = item;
    const entry = getIconEntry(tunnel.kind, tunnel.metadata.iconId);
    const wx = tunnel.position.x;
    const wy = tunnel.position.y;
    const region = findRegion(wx, wy);
    const major = findNearestLabel(wx, wy, warLabels, 'major');
    const minor = findNearestLabel(wx, wy, warLabels, 'minor');
    const short = MSUP_VARIANT_SHORT[msup.metadata.iconId] ?? msup.metadata.iconId;
    const label = tunnel.metadata.note
      ? `${tunnel.metadata.note} (${short})`
      : `Maintenance Tunnel (${short})`;

    return (
      <button
        type="button"
        onClick={() => onNavigate(wx, wy)}
        title={`Naviguer vers ${label}`}
        className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-slate-100 transition hover:bg-slate-700/60 active:bg-slate-700"
      >
        <img src={entry.src} alt="" className="mt-0.5 h-5 w-5 shrink-0 object-contain" draggable={false} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold leading-tight">
            {label}
          </span>
          <span className="block truncate text-[12px] leading-tight text-slate-300">
            {region}{major !== '—' ? ` / ${major}` : ''}
          </span>
          <span className="block truncate text-[12px] leading-tight text-slate-400">{minor}</span>
        </span>
      </button>
    );
  }

  return <AssetItem obj={item.obj} warLabels={warLabels} onNavigate={onNavigate} />;
}

interface FacilityAssetItemProps {
  item: FacilityDisplayItem;
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
}

function FacilityAssetItem({ item, warLabels, onNavigate }: FacilityAssetItemProps): React.JSX.Element {
  if (item.kind === 'merged') {
    const { material, assmats } = item;
    const entry = getIconEntry(material.kind, material.metadata.iconId);
    const wx = material.position.x;
    const wy = material.position.y;
    const region = findRegion(wx, wy);
    const major = findNearestLabel(wx, wy, warLabels, 'major');
    const minor = findNearestLabel(wx, wy, warLabels, 'minor');
    const amParts = assmats.map((a) => ASSMAT_SHORT[a.metadata.iconId] ?? a.metadata.iconId);
    const amLabel =
      amParts.length === 0
        ? ''
        : amParts[0] + (amParts.length > 1 ? '+' + amParts.slice(1).map((s) => s.replace(/^AM/, '')).join('+') : '');
    const baseName = material.metadata.note ? material.metadata.note : 'Material';
    const label = `${baseName} (${amLabel})`;
    return (
      <button
        type="button"
        onClick={() => onNavigate(wx, wy)}
        title={`Naviguer vers ${label}`}
        className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-slate-100 transition hover:bg-slate-700/60 active:bg-slate-700"
      >
        <img src={entry.src} alt="" className="mt-0.5 h-5 w-5 shrink-0 object-contain" draggable={false} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold leading-tight">{label}</span>
          <span className="block truncate text-[12px] leading-tight text-slate-300">
            {region}{major !== '—' ? ` / ${major}` : ''}
          </span>
          <span className="block truncate text-[12px] leading-tight text-slate-400">{minor}</span>
        </span>
      </button>
    );
  }
  return <AssetItem obj={item.obj} warLabels={warLabels} onNavigate={onNavigate} />;
}

interface GroupSectionProps {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /**
   * Optional content rendered above the regular children (e.g. arrow
   * visibility toggle for this category).
   */
  header?: React.ReactNode;
}

function GroupSection({
  label,
  count,
  open,
  onToggle,
  children,
  header,
}: GroupSectionProps): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between gap-2 rounded px-2 py-1 text-left text-[13px] font-semibold uppercase tracking-wide text-slate-300 transition hover:bg-slate-700/50"
      >
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="text-[10px] text-slate-500">
            {open ? '▾' : '▸'}
          </span>
          {label}
        </span>
        <span className="min-w-5 rounded bg-slate-700 px-1 text-center text-[11px] text-slate-300">
          {count}
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 pl-1">
          {header}
          {count === 0 ? (
            <p className="px-2 py-1 text-[13px] italic text-slate-500">Aucun asset</p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

interface ArrowVisibilityRowProps {
  kind: IconCategory;
  count: number;
  arrows: readonly MapArrow[];
  visible: boolean;
  onToggleVisibility: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
}

/**
 * Compact in-section header row showing the number of arrows of the given
 * `kind`. Clicking the label/count expands the row into the list of
 * individual arrows (to navigate/target one); the eye button independently
 * toggles show/hide on the interactive map (and in exports). Rendered as the
 * first child of the matching `GroupSection`.
 */
function ArrowVisibilityRow({
  kind,
  count,
  arrows,
  visible,
  onToggleVisibility,
  expanded,
  onToggleExpand,
  warLabels,
  onNavigate,
}: ArrowVisibilityRowProps): React.JSX.Element {
  const color = getCategoryStroke(kind);
  const visibleTitle = visible ? 'Masquer les flèches de cette catégorie' : 'Afficher les flèches de cette catégorie';
  const canExpand = count > 0;
  const expandTitle = !canExpand
    ? 'Aucune flèche'
    : expanded
      ? 'Replier la liste des flèches'
      : 'Déplier la liste des flèches';
  const isExpanded = expanded && canExpand;

  return (
    <div className="flex flex-col gap-0.5">
      <div
        className={`flex w-full items-center gap-1 rounded border border-dashed border-slate-700/60 px-1 py-1 text-[12px] transition ${
          visible ? 'text-slate-200' : 'text-slate-500'
        }`}
      >
        <button
          type="button"
          onClick={onToggleExpand}
          disabled={!canExpand}
          title={expandTitle}
          aria-expanded={isExpanded}
          className="flex flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left transition hover:bg-slate-700/40 disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span aria-hidden="true" className="w-2.5 shrink-0 text-center text-[10px] text-slate-500">
            {canExpand ? (isExpanded ? '▾' : '▸') : ''}
          </span>
          {/* Mini arrow glyph in the category color */}
          <svg
            aria-hidden="true"
            viewBox="0 0 16 8"
            className="h-2.5 w-4 shrink-0"
            style={{ opacity: visible ? 1 : 0.35 }}
          >
            <line x1="1" y1="4" x2="12" y2="4" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
            <polygon points="15,4 11,2 11,6" fill={color} />
          </svg>
          <span className="flex-1 truncate italic">Flèches</span>
          <span className="min-w-5 rounded bg-slate-800 px-1 text-center text-[11px] text-slate-300">
            {count}
          </span>
        </button>
        <button
          type="button"
          onClick={onToggleVisibility}
          title={visibleTitle}
          aria-pressed={visible}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[12px] text-slate-400 transition hover:bg-slate-700/60"
        >
          {visible ? '👁' : '⦸'}
        </button>
      </div>
      {isExpanded && (
        <div className="flex flex-col gap-0.5 pl-4">
          {arrows.map((arrow) => (
            <ArrowListItem key={arrow.id} arrow={arrow} warLabels={warLabels} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ArrowListItemProps {
  arrow: MapArrow;
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
}

/** A single arrow within an expanded `ArrowVisibilityRow` list, navigable like other assets. */
function ArrowListItem({ arrow, warLabels, onNavigate }: ArrowListItemProps): React.JSX.Element {
  const mid = arrow.points[Math.floor(arrow.points.length / 2)] ?? arrow.points[0];
  const wx = mid?.x ?? 0;
  const wy = mid?.y ?? 0;
  const region = findRegion(wx, wy);
  const major = findNearestLabel(wx, wy, warLabels, 'major');
  const minor = findNearestLabel(wx, wy, warLabels, 'minor');
  const note = arrow.metadata.note;
  const label = note && note.trim().length > 0 ? note : 'Flèche';
  const color = getCategoryStroke(arrow.kind);

  return (
    <button
      type="button"
      onClick={() => onNavigate(wx, wy)}
      title={`Naviguer vers ${label}`}
      className="flex w-full items-start gap-2 rounded px-2 py-1 text-left text-slate-100 transition hover:bg-slate-700/60 active:bg-slate-700"
    >
      <svg aria-hidden="true" viewBox="0 0 16 8" className="mt-1 h-2 w-3.5 shrink-0">
        <line x1="1" y1="4" x2="12" y2="4" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
        <polygon points="15,4 11,2 11,6" fill={color} />
      </svg>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-semibold leading-tight">{label}</span>
        <span className="block truncate text-[11px] leading-tight text-slate-300">
          {region}
          {major !== '—' ? ` / ${major}` : ''}
        </span>
        <span className="block truncate text-[11px] leading-tight text-slate-400">{minor}</span>
      </span>
    </button>
  );
}

const PANEL_WIDTH = 300;

const containerStyle: CSSProperties = { width: PANEL_WIDTH };

export function AssetListPanel({
  objects: rawObjects,
  arrows: rawArrows,
  warLabels,
  onNavigate,
  disabledRegionKeys,
  mode = 'planner',
}: AssetListPanelProps): React.JSX.Element {
  const isOpen = useAssetPanel((s) => s.isOpen);
  const toggle = useAssetPanel((s) => s.toggle);
  const groupOpen = useAssetPanel((s) => s.groupOpen);
  const toggleGroup = useAssetPanel((s) => s.toggleGroup);
  const arrowVisibility = useAssetPanel((s) => s.arrowVisibility);
  const toggleArrowVisibility = useAssetPanel((s) => s.toggleArrowVisibility);

  // Per-category expand/collapse of the arrow sub-list within each
  // `ArrowVisibilityRow`. Not persisted: purely a transient UI affordance.
  const [expandedArrows, setExpandedArrows] = useState<Partial<Record<IconCategory, boolean>>>({});
  const toggleArrowExpand = useCallback((kind: IconCategory) => {
    setExpandedArrows((s) => ({ ...s, [kind]: !s[kind] }));
  }, []);

  const objects = useMemo(() => {
    if (!disabledRegionKeys || disabledRegionKeys.size === 0) return rawObjects;
    return rawObjects.filter((o) => {
      const key = findRegionKey(o.position.x, o.position.y);
      return key === null || !disabledRegionKeys.has(key);
    });
  }, [rawObjects, disabledRegionKeys]);

  // Signature of all (id, note) pairs. Used as an explicit dependency of the
  // sort memos so that an edit to *any* note re-triggers the sort even if the
  // upstream array reference is reused.
  const notesSig = useMemo(
    () => objects.map((o) => `${o.id}:${o.metadata.note ?? ''}`).join('|'),
    [objects],
  );

  const HUB_ORDER: Record<string, number> = {
    LOGISTIC_HUB: 0,
    LOGISTIC_SEAPORT: 1,
    LOGISTIC_STORAGEDEPOT: 1,
    LOGISTIC_AIRWAY: 2,
  };
  const hubs = useMemo(
    () =>
      objects
        .filter(isHub)
        .slice()
        .sort((a, b) => {
          const ta = HUB_ORDER[a.metadata.iconId] ?? 99;
          const tb = HUB_ORDER[b.metadata.iconId] ?? 99;
          if (ta !== tb) return ta - tb;
          return ALPHA_COLLATOR.compare(getAssetLabel(a), getAssetLabel(b));
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objects, notesSig],
  );
  const builds = useMemo(
    () =>
      objects
        .filter(isBuilds)
        .slice()
        .sort((a, b) => ALPHA_COLLATOR.compare(getAssetLabel(a), getAssetLabel(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objects, notesSig],
  );
  const COMBAT_ORDER: Record<string, number> = {
    COMBAT_OBJECTIVE: 0,
    COMBAT_ATTACK: 1,
    COMBAT_SUPPORT: 2,
    COMBAT_PARTISAN: 3,
    COMBAT_PATROL: 4,
  };
  const targets = useMemo(
    () =>
      objects
        .filter(isCombat)
        .slice()
        .sort((a, b) => {
          const ta = COMBAT_ORDER[a.metadata.iconId] ?? 99;
          const tb = COMBAT_ORDER[b.metadata.iconId] ?? 99;
          if (ta !== tb) return ta - tb;
          return ALPHA_COLLATOR.compare(getAssetLabel(a), getAssetLabel(b));
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objects, notesSig],
  );
  const facilities = useMemo(() => objects.filter(isFacility), [objects]);
  const facilityItems = useMemo(
    () =>
      buildFacilityItems(facilities)
        .slice()
        .sort((a, b) => ALPHA_COLLATOR.compare(getFacilityItemLabel(a), getFacilityItemLabel(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facilities, notesSig],
  );
  const msup = useMemo(() => objects.filter(isMsup), [objects]);
  const msupItems = useMemo(
    () =>
      buildMsupItems(msup)
        .slice()
        .sort((a, b) => ALPHA_COLLATOR.compare(getMsupItemLabel(a), getMsupItemLabel(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [msup, notesSig],
  );
  const navy = useMemo(
    () =>
      objects
        .filter(isNavy)
        .slice()
        .sort((a, b) => ALPHA_COLLATOR.compare(getAssetLabel(a), getAssetLabel(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objects, notesSig],
  );

  // Counts per arrow category (raw, including admin-disabled regions — the
  // panel is meant to reflect what exists, while visibility toggles control
  // rendering). Empty when no arrows are provided.
  const arrowCounts = useMemo<Record<IconCategory, number>>(() => {
    const counts = ICON_CATEGORIES.reduce(
      (acc, k) => {
        acc[k] = 0;
        return acc;
      },
      {} as Record<IconCategory, number>,
    );
    if (!rawArrows) return counts;
    for (const a of rawArrows) counts[a.kind] += 1;
    return counts;
  }, [rawArrows]);

  // Arrows grouped by category, for the expandable sub-list in each
  // `ArrowVisibilityRow`.
  const arrowsByKind = useMemo<Record<IconCategory, MapArrow[]>>(() => {
    const map = ICON_CATEGORIES.reduce(
      (acc, k) => {
        acc[k] = [];
        return acc;
      },
      {} as Record<IconCategory, MapArrow[]>,
    );
    if (!rawArrows) return map;
    for (const a of rawArrows) map[a.kind].push(a);
    return map;
  }, [rawArrows]);

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={toggle}
        title="Show asset list"
        aria-label="Show asset list"
        className="absolute left-2 top-2 z-30 flex h-10 w-10 items-center justify-center rounded border border-slate-700 bg-slate-900/90 text-slate-100 shadow-lg backdrop-blur transition hover:bg-slate-700"
      >
        <span aria-hidden="true">▶</span>
      </button>
    );
  }

  // ---------------------------------------------------------------------------
  // Operation mode: simpler one-section-per-category layout.
  // ---------------------------------------------------------------------------
  if (mode === 'operation') {
    return (
      <OperationAssetSections
        objects={objects}
        warLabels={warLabels}
        onNavigate={onNavigate}
        toggle={toggle}
        groupOpen={groupOpen}
        toggleGroup={toggleGroup}
        arrowCounts={arrowCounts}
        arrowsByKind={arrowsByKind}
        arrowVisibility={arrowVisibility}
        toggleArrowVisibility={toggleArrowVisibility}
        expandedArrows={expandedArrows}
        toggleArrowExpand={toggleArrowExpand}
      />
    );
  }

  return (
    <aside
      role="complementary"
      aria-label="Asset list"
      className="absolute left-2 top-2 bottom-2 z-30 flex flex-col rounded border border-slate-700 bg-slate-900/95 text-slate-100 shadow-lg backdrop-blur"
      style={containerStyle}
    >
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-700 px-3 py-2">
        <span className="text-sm font-semibold">Assets déployés</span>
        <button
          type="button"
          onClick={toggle}
          title="Replier le panneau"
          aria-label="Collapse asset list"
          className="flex h-7 w-7 items-center justify-center rounded text-slate-300 transition hover:bg-slate-700 hover:text-slate-100"
        >
          <span aria-hidden="true">◀</span>
        </button>
      </header>

      {/* Groups */}
      <div className="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin [scrollbar-color:var(--color-slate-600)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600/70 [&::-webkit-scrollbar-thumb:hover]:bg-slate-500 [&::-webkit-scrollbar-track]:bg-transparent">
        <div className="flex flex-col gap-2">
          <GroupSection
            label="Cibles"
            count={targets.length}
            open={groupOpen.targets}
            onToggle={() => toggleGroup('targets')}
            header={
              <ArrowVisibilityRow
                kind="combat"
                count={arrowCounts.combat}
                arrows={arrowsByKind.combat}
                visible={arrowVisibility.combat}
                onToggleVisibility={() => toggleArrowVisibility('combat')}
                expanded={expandedArrows.combat ?? false}
                onToggleExpand={() => toggleArrowExpand('combat')}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            }
          >
            {targets.map((obj) => (
              <AssetItem key={obj.id} obj={obj} warLabels={warLabels} onNavigate={onNavigate} />
            ))}
          </GroupSection>

          <GroupSection
            label="Builds"
            count={builds.length}
            open={groupOpen.builds}
            onToggle={() => toggleGroup('builds')}
            header={
              <ArrowVisibilityRow
                kind="build"
                count={arrowCounts.build}
                arrows={arrowsByKind.build}
                visible={arrowVisibility.build}
                onToggleVisibility={() => toggleArrowVisibility('build')}
                expanded={expandedArrows.build ?? false}
                onToggleExpand={() => toggleArrowExpand('build')}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            }
          >
            {builds.map((obj) => (
              <AssetItem key={obj.id} obj={obj} warLabels={warLabels} onNavigate={onNavigate} />
            ))}
          </GroupSection>

          <GroupSection
            label="Facilities"
            count={facilityItems.length}
            open={groupOpen.facilities}
            onToggle={() => toggleGroup('facilities')}
            header={
              <ArrowVisibilityRow
                kind="facility"
                count={arrowCounts.facility}
                arrows={arrowsByKind.facility}
                visible={arrowVisibility.facility}
                onToggleVisibility={() => toggleArrowVisibility('facility')}
                expanded={expandedArrows.facility ?? false}
                onToggleExpand={() => toggleArrowExpand('facility')}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            }
          >
            {facilityItems.map((item) => (
              <FacilityAssetItem
                key={item.kind === 'merged' ? `merged-${item.material.id}` : item.obj.id}
                item={item}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            ))}
          </GroupSection>

          <GroupSection
            label="Logistic Hubs"
            count={hubs.length}
            open={groupOpen.hubs}
            onToggle={() => toggleGroup('hubs')}
            header={
              <ArrowVisibilityRow
                kind="logistic"
                count={arrowCounts.logistic}
                arrows={arrowsByKind.logistic}
                visible={arrowVisibility.logistic}
                onToggleVisibility={() => toggleArrowVisibility('logistic')}
                expanded={expandedArrows.logistic ?? false}
                onToggleExpand={() => toggleArrowExpand('logistic')}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            }
          >
            {hubs.map((obj) => (
              <AssetItem key={obj.id} obj={obj} warLabels={warLabels} onNavigate={onNavigate} />
            ))}
          </GroupSection>

          <GroupSection
            label="Msup"
            count={msupItems.length}
            open={groupOpen.msup}
            onToggle={() => toggleGroup('msup')}
          >
            {msupItems.map((item) => (
              <MsupAssetItem
                key={item.kind === 'merged' ? `merged-${item.tunnel.id}` : item.obj.id}
                item={item}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            ))}
          </GroupSection>

          <GroupSection
            label="Navy"
            count={navy.length}
            open={groupOpen.navy}
            onToggle={() => toggleGroup('navy')}
            header={
              <ArrowVisibilityRow
                kind="navy"
                count={arrowCounts.navy}
                arrows={arrowsByKind.navy}
                visible={arrowVisibility.navy}
                onToggleVisibility={() => toggleArrowVisibility('navy')}
                expanded={expandedArrows.navy ?? false}
                onToggleExpand={() => toggleArrowExpand('navy')}
                warLabels={warLabels}
                onNavigate={onNavigate}
              />
            }
          >
            {navy.map((obj) => (
              <AssetItem key={obj.id} obj={obj} warLabels={warLabels} onNavigate={onNavigate} />
            ))}
          </GroupSection>
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Operation-mode panel: one section per op_* category with the same item
// rendering as the planner panel (no merging). Lives in the same file so
// it shares the AssetItem / GroupSection helpers.
// ---------------------------------------------------------------------------

interface OperationAssetSectionsProps {
  objects: readonly MapObject[];
  warLabels: readonly WarLabel[];
  onNavigate: (wx: number, wy: number) => void;
  toggle: () => void;
  groupOpen: Record<AssetGroupKey, boolean>;
  toggleGroup: (group: AssetGroupKey) => void;
  arrowCounts: Record<IconCategory, number>;
  arrowsByKind: Record<IconCategory, MapArrow[]>;
  arrowVisibility: Record<IconCategory, boolean>;
  toggleArrowVisibility: (kind: IconCategory) => void;
  expandedArrows: Partial<Record<IconCategory, boolean>>;
  toggleArrowExpand: (kind: IconCategory) => void;
}

const OPERATION_SECTIONS: ReadonlyArray<{
  kind: IconCategory;
  groupKey: AssetGroupKey;
}> = [
  { kind: 'op_info', groupKey: 'op_info' },
  { kind: 'op_attack', groupKey: 'op_attack' },
  { kind: 'op_defend', groupKey: 'op_defend' },
  { kind: 'op_arty', groupKey: 'op_arty' },
  { kind: 'op_build', groupKey: 'op_build' },
  { kind: 'op_navy', groupKey: 'op_navy' },
  { kind: 'op_unit', groupKey: 'op_unit' },
  { kind: 'op_vehicle', groupKey: 'op_vehicle' },
];

function OperationAssetSections({
  objects,
  warLabels,
  onNavigate,
  toggle,
  groupOpen,
  toggleGroup,
  arrowCounts,
  arrowsByKind,
  arrowVisibility,
  toggleArrowVisibility,
  expandedArrows,
  toggleArrowExpand,
}: OperationAssetSectionsProps): React.JSX.Element {
  // Group objects by category and sort by label.
  const byKind = useMemo(() => {
    const map = new Map<IconCategory, MapObject[]>();
    for (const section of OPERATION_SECTIONS) {
      map.set(section.kind, []);
    }
    for (const o of objects) {
      const list = map.get(o.kind);
      if (list) list.push(o);
    }
    for (const list of map.values()) {
      list.sort((a, b) => ALPHA_COLLATOR.compare(getAssetLabel(a), getAssetLabel(b)));
    }
    return map;
  }, [objects]);

  return (
    <aside
      role="complementary"
      aria-label="Asset list (operation)"
      className="absolute left-2 top-2 bottom-2 z-30 flex flex-col rounded border border-slate-700 bg-slate-900/95 text-slate-100 shadow-lg backdrop-blur"
      style={containerStyle}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-700 px-3 py-2">
        <span className="text-sm font-semibold">Assets opération</span>
        <button
          type="button"
          onClick={toggle}
          title="Replier le panneau"
          aria-label="Collapse asset list"
          className="flex h-7 w-7 items-center justify-center rounded text-slate-300 transition hover:bg-slate-700 hover:text-slate-100"
        >
          <span aria-hidden="true">◀</span>
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin [scrollbar-color:var(--color-slate-600)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600/70 [&::-webkit-scrollbar-thumb:hover]:bg-slate-500 [&::-webkit-scrollbar-track]:bg-transparent">
        <div className="flex flex-col gap-2">
          {OPERATION_SECTIONS.map((section) => {
            const items = byKind.get(section.kind) ?? [];
            const label = ICON_CATALOG[section.kind].label;
            return (
              <GroupSection
                key={section.groupKey}
                label={label}
                count={items.length}
                open={groupOpen[section.groupKey]}
                onToggle={() => toggleGroup(section.groupKey)}
                header={
                  <ArrowVisibilityRow
                    kind={section.kind}
                    count={arrowCounts[section.kind]}
                    arrows={arrowsByKind[section.kind]}
                    visible={arrowVisibility[section.kind]}
                    onToggleVisibility={() => toggleArrowVisibility(section.kind)}
                    expanded={expandedArrows[section.kind] ?? false}
                    onToggleExpand={() => toggleArrowExpand(section.kind)}
                    warLabels={warLabels}
                    onNavigate={onNavigate}
                  />
                }
              >
                {items.map((obj) => (
                  <AssetItem
                    key={obj.id}
                    obj={obj}
                    warLabels={warLabels}
                    onNavigate={onNavigate}
                  />
                ))}
              </GroupSection>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
